import { useEffect, useRef, useState } from 'react';
import { Input, Skeleton } from '@bbpos/ui';
import { tenantService } from '@/services/tenant.service';
import { cn } from '@/constants/design-tokens';

interface TenantOption {
  id: string;
  business_name: string;
}

interface TenantPickerProps {
  value: TenantOption | null;
  onChange: (option: TenantOption | null) => void;
  disabled?: boolean;
}

/**
 * Debounced async search combobox for the "target a specific tenant"
 * flow on the Broadcast Compose form. Intentionally minimal — the list
 * renders inline under the input rather than in a popover, because the
 * form is already column-scoped and the extra layering added little.
 */
export function TenantPicker({ value, onChange, disabled = false }: TenantPickerProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<TenantOption[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showList, setShowList] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!showList) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const res = await tenantService.list({
          page: 1,
          limit: 8,
          status: 'ACTIVE',
          search: query.trim() || undefined,
        });
        setResults(
          res.data.map((t) => ({ id: t.id, business_name: t.business_name })),
        );
      } catch {
        setResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 260);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, showList]);

  if (value) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-xl bg-white/70 dark:bg-neutral-800/60 backdrop-blur-sm px-4 py-2.5 ring-1 ring-inset ring-neutral-200/70 dark:ring-neutral-700/60">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wider text-neutral-500 dark:text-neutral-400 font-semibold">
            Target tenant
          </p>
          <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-50 truncate tabular-nums">
            {value.business_name}
          </p>
          <p className="text-[11px] text-neutral-500 dark:text-neutral-400 font-mono mt-0.5 truncate">
            {value.id}
          </p>
        </div>
        {!disabled && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-[11px] font-semibold text-brand-600 dark:text-brand-300 hover:underline shrink-0"
          >
            Change
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Input
        variant="search"
        placeholder="Search tenants by business name…"
        value={query}
        onChange={setQuery}
        disabled={disabled}
        onKeyDown={() => setShowList(true)}
        aria-label="Search tenants"
      />
      {showList && (
        <div className="rounded-xl bg-white/80 dark:bg-neutral-800/70 backdrop-blur-sm shadow-[0_8px_24px_-14px_rgba(15,23,42,0.18)] overflow-hidden">
          {isSearching && (
            <div className="p-3 space-y-2">
              <Skeleton variant="text" width="60%" height={12} />
              <Skeleton variant="text" width="40%" height={12} />
            </div>
          )}
          {!isSearching && results.length === 0 && (
            <p className="px-4 py-3 text-xs text-neutral-500 dark:text-neutral-400">
              No matching tenants.
            </p>
          )}
          {!isSearching &&
            results.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  onChange(t);
                  setShowList(false);
                  setQuery('');
                }}
                className={cn(
                  'w-full text-left px-4 py-2.5 hover:bg-brand-50/60 dark:hover:bg-brand-900/20 transition-colors',
                  'focus-visible:bg-brand-50 dark:focus-visible:bg-brand-900/30 focus-visible:outline-none',
                )}
              >
                <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate">
                  {t.business_name}
                </p>
                <p className="text-[11px] text-neutral-500 dark:text-neutral-400 font-mono truncate">
                  {t.id}
                </p>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}
